import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../utils/api';
import { 
  Users, 
  Activity, 
  Ticket, 
  CreditCard, 
  TrendingUp, 
  Shield,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Zap,
  BarChart3,
  PieChart,
  Wifi,
  Server
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell
} from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_users: 0,
    active_sessions: 0,
    unused_vouchers: 0,
    total_plans: 0,
    daily_sessions: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();

    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await adminAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'WireGuard Peers', 
      value: stats.total_users, 
      icon: Users, 
      meta: '+12% from last week',
      trend: 'up',
      color: 'blue',
      bgGradient: 'from-blue-500 to-blue-600'
    },
    { 
      title: 'Active Sessions', 
      value: stats.active_sessions, 
      icon: Activity, 
      meta: 'Live connections',
      trend: 'up',
      color: 'green',
      bgGradient: 'from-green-500 to-green-600'
    },
    { 
      title: 'Available Vouchers', 
      value: stats.unused_vouchers, 
      icon: Ticket, 
      meta: 'Ready to distribute',
      trend: 'stable',
      color: 'purple',
      bgGradient: 'from-purple-500 to-purple-600'
    },
    { 
      title: 'Active Plans', 
      value: stats.total_plans, 
      icon: CreditCard, 
      meta: 'Service tiers',
      trend: 'stable',
      color: 'orange',
      bgGradient: 'from-orange-500 to-orange-600'
    },
  ];

  // Prepare chart data
  const chartData = stats.daily_sessions.map((day) => ({
    date: day.date,
    sessions: day.sessions,
  }));

  // Mock pie chart data for service distribution
  const serviceDistribution = [
    { name: 'Basic', value: 45, color: '#3b82f6' },
    { name: 'Premium', value: 30, color: '#8b5cf6' },
    { name: 'Enterprise', value: 25, color: '#06b6d4' }
  ];

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return <div className="w-1 h-1 bg-gray-400 rounded-full" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Network monitoring and management</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                <Clock className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-600">Auto-refresh: 30s</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-700 font-medium">System Online</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {statCards.map(({ title, value, icon: Icon, meta, trend, color, bgGradient }) => (
            <div key={title} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">{title}</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-2">
                    {loading ? (
                      <span className="inline-block w-12 h-6 bg-gray-100 rounded animate-pulse"></span>
                    ) : (
                      Number(value || 0).toLocaleString()
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{meta}</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <Icon className="h-5 w-5 text-gray-600" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
          {/* Session Analytics Chart */}
          <div className="xl:col-span-2 bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Session Analytics</h2>
                <p className="text-xs text-gray-500 mt-0.5">Daily activity over the last 7 days</p>
              </div>
              <div className="flex items-center gap-1">
                <button className="px-2.5 py-1 text-xs font-medium bg-gray-900 text-white rounded hover:bg-gray-800 transition">
                  7 Days
                </button>
                <button className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 rounded transition">
                  30 Days
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="h-56 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#111827" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#111827" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9ca3af"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#9ca3af"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="sessions" 
                    stroke="#111827" 
                    strokeWidth={1.5}
                    fill="url(#colorSessions)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-56 flex flex-col items-center justify-center">
                <BarChart3 className="w-8 h-8 mb-2 text-gray-400" />
                <p className="text-sm text-gray-500">No session data available</p>
                <p className="text-xs text-gray-400 mt-1">Data will appear once sessions are recorded</p>
              </div>
            )}
          </div>

          {/* Service Distribution Pie Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Plan Distribution</h2>
                <p className="text-xs text-gray-500 mt-0.5">Active user breakdown</p>
              </div>
              <PieChart className="w-4 h-4 text-gray-400" />
            </div>
            
            <ResponsiveContainer width="100%" height={180}>
              <RePieChart>
                <Pie
                  data={serviceDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {serviceDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RePieChart>
            </ResponsiveContainer>
            
            <div className="mt-4 space-y-2">
              {serviceDistribution.map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-sm text-gray-600">{item.name}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions & System Status */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Quick Actions</h2>
              <Shield className="w-4 h-4 text-gray-400" />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => { window.location.href = '/admin/vouchers'; }}
                className="flex flex-col items-center justify-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Ticket className="w-6 h-6 text-gray-700 mb-2" />
                <p className="text-sm font-medium text-gray-900">Vouchers</p>
                <p className="text-xs text-gray-500 mt-0.5">Generate codes</p>
              </button>
              
              <button 
                onClick={() => { window.location.href = '/admin/users'; }}
                className="flex flex-col items-center justify-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Users className="w-6 h-6 text-gray-700 mb-2" />
                <p className="text-sm font-medium text-gray-900">WG Peers</p>
                <p className="text-xs text-gray-500 mt-0.5">Manage</p>
              </button>
              
              <button 
                onClick={() => { window.location.href = '/admin/plans'; }}
                className="flex flex-col items-center justify-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <CreditCard className="w-6 h-6 text-gray-700 mb-2" />
                <p className="text-sm font-medium text-gray-900">Plans</p>
                <p className="text-xs text-gray-500 mt-0.5">Configure</p>
              </button>
              
              <button 
                onClick={() => { window.location.href = '/admin/sessions'; }}
                className="flex flex-col items-center justify-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Activity className="w-6 h-6 text-gray-700 mb-2" />
                <p className="text-sm font-medium text-gray-900">Sessions</p>
                <p className="text-xs text-gray-500 mt-0.5">Monitor</p>
              </button>
            </div>
          </div>

          {/* System Status */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">System Health</h2>
              <Server className="w-4 h-4 text-gray-400" />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-700">WireGuard Service</span>
                </div>
                <span className="text-xs font-medium text-green-600">ACTIVE</span>
              </div>
              
              <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-700">Database</span>
                </div>
                <span className="text-xs font-medium text-green-600">CONNECTED</span>
              </div>
              
              <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-700">API Server</span>
                </div>
                <span className="text-xs font-medium text-green-600">RUNNING</span>
              </div>
              
              <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">MDM Service</span>
                </div>
                <span className="text-xs font-medium text-yellow-600">OPTIONAL</span>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Wifi className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-sm font-medium text-gray-900">Network Status</span>
              </div>
              <p className="text-xs text-gray-600 mt-1">All systems operational</p>
              <div className="mt-1.5 text-xs text-gray-500">Last checked: Just now</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;